"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TEMPORAL_PROFILE_ROLE_PRIMARY_TYPE = exports.TEMPORAL_PROFILE_ROLE_FIELDS = exports.TEMPORAL_PROFILE_ROLES = exports.TEMPORAL_PROFILE_VIEW_TYPES = void 0;
exports.isTemporalProfileViewType = isTemporalProfileViewType;
exports.temporalProfileRoleColumnId = temporalProfileRoleColumnId;
exports.temporalProfileRoleAcceptsPropertyType = temporalProfileRoleAcceptsPropertyType;
exports.resolveTemporalProfileColumn = resolveTemporalProfileColumn;
exports.resolveTemporalDateColumns = resolveTemporalDateColumns;
/**
 * The roles a calendar or gantt view gives the columns of its database
 * (start, end, all day ...), and which column takes a role in a new view.
 * The app (data-view calendar/gantt define.ts and view managers) and the
 * server (database-schema.service createView) resolve the same columns.
 */
exports.TEMPORAL_PROFILE_VIEW_TYPES = ['calendar', 'gantt'];
exports.TEMPORAL_PROFILE_ROLES = [
    'title',
    'start',
    'end',
    'allDay',
    'timezone',
    'calendarId',
    'status',
    'visibility',
    'attendees',
    'assignee',
    'progress',
    'dependencies',
    'locationPhysical',
    'locationVirtual',
    'description',
    'reminder',
    'recurrence',
    'recurrenceExdate',
    'recurrenceId',
];
exports.TEMPORAL_PROFILE_ROLE_FIELDS = {
    allDay: ['allDayCol'],
    assignee: ['assigneeCol'],
    attendees: ['attendeesCol'],
    calendarId: ['calendarIdCol'],
    dependencies: ['dependenciesCol'],
    description: ['descriptionCol'],
    end: ['endDateCol'],
    locationPhysical: ['locationPhysicalCol'],
    locationVirtual: ['locationVirtualCol'],
    progress: ['progressCol'],
    recurrence: ['recurrenceCol'],
    recurrenceExdate: ['recurrenceExdateCol'],
    recurrenceId: ['recurrenceIdCol'],
    reminder: ['reminderCol'],
    start: ['startDateCol'],
    status: ['statusCol'],
    timezone: ['timezoneCol'],
    title: ['header.titleColumn'],
    visibility: ['visibilityCol'],
};
exports.TEMPORAL_PROFILE_ROLE_PRIMARY_TYPE = {
    allDay: 'checkbox',
    assignee: 'member',
    attendees: 'rich-text',
    calendarId: 'select',
    dependencies: 'relation',
    description: 'rich-text',
    end: 'date',
    locationPhysical: 'rich-text',
    locationVirtual: 'link',
    progress: 'progress',
    recurrence: 'rich-text',
    recurrenceExdate: 'rich-text',
    recurrenceId: 'rich-text',
    reminder: 'rich-text',
    start: 'date',
    status: 'select',
    timezone: 'rich-text',
    title: 'title',
    visibility: 'select',
};
const TEMPORAL_PROFILE_NAME_PATTERNS = {
    allDay: /\b(all.?day|ganztag)\b/i,
    assignee: /\b(assign(ee|ed)?|owner|responsible|lead|user|member|person)\b/i,
    attendees: /\b(attendees|guests|invite|teilnehmer)\b/i,
    calendarId: /\b(calendar|kalender|resource)\b/i,
    dependencies: /\b(depend(enc(y|ies))?|block(ed|s|ing)?|predecessor|successor|requires)\b/i,
    description: /\b(description|notes|details|beschreibung)\b/i,
    end: /\b(end|finish|due|to|deadline|target)\b/i,
    locationPhysical: /\b(address|ort|location|place)\b/i,
    locationVirtual: /\b(zoom|teams|meet|link|url|virtual)\b/i,
    progress: /\b(progress|percent|complete|completion|done|%)\b/i,
    recurrence: /\b(recurr|repeat|rrule|wiederhol)\b/i,
    recurrenceExdate: /\b(exdate|exception|ausnahme)\b/i,
    recurrenceId: /\b(recurrence.?id|recurrence-id)\b/i,
    reminder: /\b(reminder|alert|erinner)\b/i,
    start: /\b(start|begin|from|kick.?off)\b/i,
    status: /\b(status|state|phase|stage|zustand)\b/i,
    timezone: /\b(time.?zone|tz|zeitzone)\b/i,
    title: /\b(title|name|titel)\b/i,
    visibility: /\b(visibility|sichtbarkeit)\b/i,
};
function isTemporalProfileViewType(type) {
    return type === 'calendar' || type === 'gantt';
}
function temporalProfileRoleColumnId(viewData, role) {
    if (!viewData || !isTemporalProfileViewType(viewData.mode))
        return undefined;
    for (const field of exports.TEMPORAL_PROFILE_ROLE_FIELDS[role]) {
        const value = readPath(viewData, field);
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
    }
    return undefined;
}
function temporalProfileRoleAcceptsPropertyType(role, propertyType, propertyData, selfBlockId) {
    if (!propertyType)
        return false;
    if (role === 'title')
        return propertyType === 'title';
    if (role === 'start' || role === 'end')
        return propertyType === 'date';
    if (role === 'allDay')
        return propertyType === 'checkbox';
    if (role === 'assignee')
        return propertyType === 'member';
    if (role === 'progress')
        return propertyType === 'progress';
    if (role === 'dependencies') {
        if (propertyType !== 'relation')
            return false;
        if (!selfBlockId)
            return true;
        return propertyData?.targetDatabaseId === selfBlockId;
    }
    if (role === 'calendarId' || role === 'status' || role === 'visibility') {
        return propertyType === 'select' || propertyType === 'rich-text';
    }
    if (role === 'locationVirtual') {
        return propertyType === 'link' || propertyType === 'rich-text';
    }
    return propertyType === 'rich-text';
}
function resolveTemporalProfileColumn(input) {
    const { currentId, dataSource, role, selfBlockId, views = [] } = input;
    const excludeIds = new Set(input.excludeIds ?? []);
    const existing = new Set(dataSource.getAllPropertyIds());
    if (isUsableTemporalProfileColumn(dataSource, role, currentId, existing, {
        excludeIds,
        selfBlockId,
    })) {
        return currentId;
    }
    for (const viewData of views) {
        const propertyId = temporalProfileRoleColumnId(viewData, role);
        if (isUsableTemporalProfileColumn(dataSource, role, propertyId, existing, {
            excludeIds,
            selfBlockId,
        })) {
            return propertyId;
        }
    }
    return dataSource.getAllPropertyIds().find(propertyId => {
        if (excludeIds.has(propertyId))
            return false;
        if (!temporalProfileRoleAcceptsPropertyType(role, dataSource.propertyTypeGet(propertyId), dataSource.propertyDataGet?.(propertyId), selfBlockId)) {
            return false;
        }
        return TEMPORAL_PROFILE_NAME_PATTERNS[role].test(dataSource.propertyNameGet(propertyId));
    });
}
function isUsableTemporalProfileColumn(dataSource, role, propertyId, existing, options) {
    if (!propertyId || !existing.has(propertyId))
        return false;
    if (options.excludeIds.has(propertyId))
        return false;
    return temporalProfileRoleAcceptsPropertyType(role, dataSource.propertyTypeGet(propertyId), dataSource.propertyDataGet?.(propertyId), options.selfBlockId);
}
function readPath(source, path) {
    return path.split('.').reduce((value, key) => {
        if (!value || typeof value !== 'object')
            return undefined;
        return value[key];
    }, source);
}
/**
 * Start and end of a new calendar or gantt: the columns that take these
 * roles (another temporal view's, else one named for the role), else the
 * database's date columns in order. Undefined when the database has none.
 */
function resolveTemporalDateColumns(dataSource, views) {
    const dateColumns = dataSource
        .getAllPropertyIds()
        .filter(id => dataSource.propertyTypeGet(id) === 'date');
    const startDateCol = resolveTemporalProfileColumn({ dataSource, role: 'start', views }) ??
        dateColumns[0];
    const endDateCol = resolveTemporalProfileColumn({
        dataSource,
        excludeIds: startDateCol ? [startDateCol] : [],
        role: 'end',
        views,
    }) ?? dateColumns.find(id => id !== startDateCol);
    return { startDateCol, endDateCol };
}
